import { apiGet, apiPatch, apiPost } from './client.js';

export type PartFailure = {
  id: string;
  partName: string;
  partSku?: string | null;
  unitCost: string;
  failureType: string;
  failureReason?: string | null;
  disposition: string;
  daysToFailure?: number | null;
  installedAt?: string | null;
  failedAt: string;
  supplierId?: string | null;
  supplierName?: string | null;
  inventoryItemId?: string | null;
  inventorySerialId?: string | null;
  warrantyTicketId?: string | null;
  warrantyTicketNumber?: string | null;
  sourceTicketNumber?: string | null;
  installedTicketId?: string | null;
  reportedByName?: string | null;
  attributedTo?: string | null;
  attributedToName?: string | null;
  reviewedAt?: string | null;
  dispositionNote?: string | null;
  rmaItemId?: string | null;
};

export type InstalledPartOption = {
  id: string;
  partName: string;
  partSku?: string | null;
  unitCost: string;
  supplierName?: string | null;
  installedAt: string;
  originalTicketNumber: string;
};

export type QuarantineBin = {
  id: string;
  name: string;
  supplierId?: string | null;
  supplierName?: string | null;
  location?: string | null;
  active: boolean;
};

export const FAILURE_TYPES = [
  { value: 'doa', label: 'DOA' },
  { value: 'early_failure', label: 'Early failure' },
  { value: 'customer_return', label: 'Customer return' },
  { value: 'tech_damage', label: 'Tech damage' },
  { value: 'shipping_damage', label: 'Shipping damage' },
  { value: 'failed_testing', label: 'Failed testing' },
  { value: 'other', label: 'Other' },
] as const;

export function createWarrantyTicket(
  ticketId: string,
  body: { warrantyReason: string; repairIds: string[] },
) {
  return apiPost<Record<string, unknown>>(`/api/v1/tickets/${ticketId}/warranty`, body).then((r) => r.data);
}

export function listInstalledParts(ticketId: string) {
  return apiGet<InstalledPartOption[]>(`/api/v1/tickets/${ticketId}/installed-parts`).then((r) => r.data);
}

export function recordInstalledPartFailure(
  ticketId: string,
  body: {
    installedTicketPartId: string;
    failureType: string;
    failureReason?: string | undefined;
    quarantineBinId?: string | undefined;
    disposition?: string;
    attributedTo?: string | undefined;
  },
) {
  return apiPost<PartFailure>(`/api/v1/tickets/${ticketId}/part-failures`, body).then((r) => r.data);
}

export function listQuarantineBins() {
  return apiGet<QuarantineBin[]>('/api/v1/inventory/quarantine-bins').then((r) => r.data);
}

export function createQuarantineBin(body: {
  name: string;
  supplierId?: string | null;
  location?: string | null;
}) {
  return apiPost<QuarantineBin>('/api/v1/inventory/quarantine-bins', body).then((r) => r.data);
}

export function listDamageReports(params: Record<string, string | number | undefined>) {
  return apiGet<PartFailure[]>('/api/v1/inventory/damage-reports', { params });
}

export function recordDamageReport(body: {
  inventoryItemId: string;
  inventorySerialId?: string | undefined;
  quantity?: number;
  failureType: string;
  failureReason?: string | undefined;
  supplierId?: string | undefined;
  unitCost?: number | undefined;
  quarantineBinId?: string | undefined;
  disposition?: string;
  attributedTo?: string | undefined;
  partName?: string | undefined;
}) {
  return apiPost<PartFailure[]>('/api/v1/inventory/damage-reports', body).then((r) => r.data);
}

export function reclassifyDamageReport(
  id: string,
  body: { disposition: 'quarantine' | 'rma' | 'write_off'; note: string },
) {
  return apiPatch<PartFailure>(`/api/v1/inventory/damage-reports/${id}`, body).then((r) => r.data);
}

export function listQuarantineItems(supplierId: string) {
  return apiGet<PartFailure[]>('/api/v1/inventory/quarantine-items', { params: { supplierId } }).then(
    (r) => r.data,
  );
}

export function listQuarantineSummary() {
  return apiGet<Array<{ supplierId: string | null; supplierName: string; count: number; totalCost: string }>>(
    '/api/v1/inventory/quarantine-summary',
  ).then((r) => r.data);
}
